import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment, faPaperPlane, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { joinRideRoom, leaveRideRoom, onSocketEvent, offSocketEvent } from "../../services/socket";
import "./chat.css";

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const Chat = ({ rideId, loginUser }) => {
  const [searchParams] = useSearchParams();
  const deepLinked = searchParams.get("chat") === "1";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  // Opened from the inbox → start expanded; otherwise collapsed by default.
  const [collapsed, setCollapsed] = useState(!deepLinked);
  const messagesEndRef = useRef(null);
  const rootRef = useRef(null);

  // Scroll the chat into view when arrived at via an inbox deep-link.
  useEffect(() => {
    if (deepLinked) {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [deepLinked]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Mark messages as read
  const markAsRead = useCallback(async () => {
    try {
      await axios.put(`/api/v1/messages/${rideId}/read`);
    } catch (err) {
      // Silent — read receipts are non-critical
    }
  }, [rideId]);

  // Fetch chat history
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const { data } = await axios.get(`/api/v1/messages/${rideId}`);
        setMessages(data.messages);
        markAsRead();
      } catch (err) {
        // Silent — chat is optional
      }
    };
    fetchMessages();
  }, [rideId, markAsRead]);

  // Join ride room + listen for new messages
  useEffect(() => {
    joinRideRoom(rideId);

    const handleNewMessage = (msg) => {
      setMessages((prev) => {
        // Avoid duplicates (optimistic + socket)
        if (prev.some((m) => m._id === msg._id)) return prev;
        return [...prev, msg];
      });
      // Mark as read if the message is from someone else
      if (msg.sender._id !== loginUser._id) {
        markAsRead();
      }
    };

    const handleMessagesRead = ({ userId }) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.sender._id !== loginUser._id) return msg;
          if (msg.readBy && msg.readBy.includes(userId)) return msg;
          return { ...msg, readBy: [...(msg.readBy || []), userId] };
        })
      );
    };

    onSocketEvent("new-message", handleNewMessage);
    onSocketEvent("messages-read", handleMessagesRead);

    return () => {
      leaveRideRoom(rideId);
      offSocketEvent("new-message", handleNewMessage);
      offSocketEvent("messages-read", handleMessagesRead);
    };
  }, [rideId, loginUser._id, markAsRead]);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || sending) return;

    // Expand chat if collapsed when sending
    if (collapsed) setCollapsed(false);

    // Optimistic UI
    const tempMsg = {
      _id: "temp-" + Date.now(),
      sender: { _id: loginUser._id, userName: loginUser.userName, avatar: loginUser.avatar },
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);
    setInput("");
    setSending(true);

    try {
      const { data } = await axios.post(`/api/v1/messages/${rideId}`, { content });
      // Replace temp message with server response
      setMessages((prev) =>
        prev.map((m) => (m._id === tempMsg._id ? data.message : m))
      );
    } catch (err) {
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m._id !== tempMsg._id));
    } finally {
      setSending(false);
    }
  };

  return (
    <div ref={rootRef} className={`sr__chat ${collapsed ? "sr__chat--collapsed" : ""}`}>
      <div className="sr__chat__header" onClick={() => setCollapsed(!collapsed)}>
        <span className="sr__chat__header-icon">
          <FontAwesomeIcon icon={faComment} />
        </span>
        <h3>Chat</h3>
        {messages.length > 0 && (
          <span className="sr__chat__header-badge">{messages.length}</span>
        )}
        <span className="sr__chat__header-toggle">
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
      </div>

      <div className="sr__chat__body">
        <div className="sr__chat__messages">
          {messages.length === 0 ? (
            <p className="sr__chat__empty">
              <span className="sr__chat__empty-icon">
                <FontAwesomeIcon icon={faComment} />
              </span>
              No messages yet. Start the conversation!
            </p>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender._id === loginUser._id;
              return (
                <div
                  key={msg._id}
                  className={`sr__chat__msg ${isOwn ? "sr__chat__msg--own" : "sr__chat__msg--other"}`}
                >
                  {!isOwn && (
                    <img
                      src={msg.sender.avatar}
                      alt=""
                      className="sr__chat__msg__avatar"
                    />
                  )}
                  <div>
                    {!isOwn && (
                      <div className="sr__chat__msg__name">{msg.sender.userName}</div>
                    )}
                    <div className="sr__chat__msg__bubble">{msg.content}</div>
                    <div className="sr__chat__msg__time">
                      {formatTime(msg.createdAt)}
                      {isOwn && (
                        <span className={`sr__chat__msg__checks${msg.readBy && msg.readBy.length > 0 ? " sr__chat__msg__checks--read" : ""}`}>
                          {msg.readBy && msg.readBy.length > 0 ? "\u2713\u2713" : "\u2713"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="sr__chat__input" onSubmit={handleSend}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message..."
            maxLength={1000}
          />
          <button type="submit" disabled={!input.trim() || sending} aria-label="Send message">
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
